import { useCallback, useEffect, useState } from "react";
import i18n from "src/i18n/config";
import { ga4DashboardService } from "src/services/GA4DashboardAPIs";
import { TAB_ID_TO_METRIC_SELECTOR, type TabData, type TabId } from "../types/tabs";
import type { ApiLang } from "src/utils/apiLang";

/** Max entries the top-locations panel can display; also the value sent as the API `limit`. */
export const MAX_TOP_LOCATIONS = 20;
/** Default "Show" count wherever the top-locations panel appears — user can raise it up to MAX_TOP_LOCATIONS. */
export const DEFAULT_TOP_LOCATIONS_DISPLAY_COUNT = 10;

const EMPTY_TAB_DATA: TabData = {
  demographics: { regions: [], ages: [], gender: [], device: [] },
  channels: [],
  topLocations: { city: [], country: [] },
};

export function useTabsData(
  startDate: string,
  endDate: string,
  activeTab: TabId,
  lang: ApiLang
) {
  const [data, setData] = useState<TabData>(EMPTY_TAB_DATA);
  const [rawData, setRawData] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTabsData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRawData(null);
    try {
      const result = await ga4DashboardService.fetchTabsKPI({
        start_date: startDate,
        end_date: endDate,
        metric_selector: TAB_ID_TO_METRIC_SELECTOR[activeTab],
        lang,
        limit: MAX_TOP_LOCATIONS,
      });
      setRawData(result);
      setData(result ?? EMPTY_TAB_DATA);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : i18n.t("webAnalytics.errors.loadTabsFailed")
      );
      setRawData(null);
      setData(EMPTY_TAB_DATA);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, activeTab, lang]);

  useEffect(() => {
    fetchTabsData();
  }, [fetchTabsData]);

  return { data, rawData, loading, error };
}
